import { KeyboardEvent, useId, useRef } from 'react';
import './styles/NoticeThemePicker.scss';

import { Box, Stack, Typography } from '@mui/material';
import { Check } from '@mui/icons-material';

import { ISchoolNoticeTheme, noticeThemeBorderSx, noticeThemeName } from '@dw/models/notice';
import { componentClassName } from '@dw/utils';

import { HStack } from '@dw/components/core';

const cn = componentClassName('notice-theme-picker');

interface INoticeThemePickerProps {
  themes: ISchoolNoticeTheme[];
  value?: string;
  onChange: (noticeThemeId: string) => void;
  label?: string;
  disabled?: boolean;
}

/** The next option, wrapping past either end. */
const nextIndex = (count: number, fromIndex: number, step: number): number =>
  (((fromIndex + step) % count) + count) % count;

/**
 * Pick the colour a notice goes on, by looking at it.
 *
 * Not a `SelectionTileGroup`, and the reason is structural rather than stylistic: that control never
 * wraps (`HStack breakpoint={false}`) and is documented for two-to-four options, because its tiles
 * exist to be compared side by side. A palette is open-ended — seven to begin with and however many
 * a studio adds — so its options have to wrap, and at phone width they must wrap rather than squeeze
 * seven tiles into a row.
 *
 * Each chip is the theme itself: its paper, its edge, its ink, carrying its own name. That makes a
 * theme whose text is illegible on its own background announce itself here, in the picker, rather
 * than on a family's phone.
 *
 * A real radio group: one tab stop for the whole set, arrows to move (selecting as they go, as
 * radios do), Home/End for the ends, Space/Enter to confirm.
 */
export const NoticeThemePicker = (props: INoticeThemePickerProps) => {

  const { themes, value, onChange, label, disabled } = props;

  const groupRef = useRef<HTMLDivElement>(null);
  const labelId = useId();

  const selectedIndex = themes.findIndex(x => x.noticeThemeId === value);
  // The roving tab stop: the whole group is ONE stop, on the selected chip — or the first, so the
  // group is never skipped entirely when nothing is selected yet.
  const tabStopIndex = selectedIndex >= 0 ? selectedIndex : 0;

  const selectAndFocus = (index: number) => {
    onChange(themes[index].noticeThemeId);
    groupRef.current?.querySelector<HTMLElement>(`[data-theme-index='${index}']`)?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>, index: number) => {
    if (disabled || themes.length === 0) {
      return;
    }

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        selectAndFocus(nextIndex(themes.length, index, 1));
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        selectAndFocus(nextIndex(themes.length, index, -1));
        break;
      case 'Home':
        event.preventDefault();
        selectAndFocus(0);
        break;
      case 'End':
        event.preventDefault();
        selectAndFocus(themes.length - 1);
        break;
      case ' ':
      case 'Enter':
        event.preventDefault();
        onChange(themes[index].noticeThemeId);
        break;
    }
  };

  return (
    <Stack ref={groupRef} rowGap={1} className={cn()}>
      {!!label && (
        <Typography id={labelId} variant='overline' color='textDisabled' fontWeight={600} lineHeight={1.4}>
          {label}
        </Typography>
      )}

      <HStack
        breakpoint={false}
        columnGap={1}
        rowGap={1}
        flexWrap='wrap'
        role='radiogroup'
        aria-labelledby={label ? labelId : undefined}
      >
        {themes.map((theme, index) => {
          const isSelected = theme.noticeThemeId === value;

          return (
            <Box
              key={theme.noticeThemeId}
              className={cn('chip')}
              data-theme-index={index}
              role='radio'
              aria-checked={isSelected}
              aria-disabled={!!disabled}
              aria-label={noticeThemeName(theme)}
              tabIndex={index === tabStopIndex ? 0 : -1}
              onClick={() => { if (!disabled) onChange(theme.noticeThemeId); }}
              onKeyDown={(event) => handleKeyDown(event, index)}
              sx={{
                backgroundColor: theme.backgroundColor,
                color: theme.textColor,
                // The theme's own edge when it has one; the stylesheet's faint default when it does
                // not, so a near-white chip never reads as a hole in the dialog.
                ...noticeThemeBorderSx(theme)
              }}
            >
              <HStack breakpoint={false} columnGap={1} alignItems='center' justifyContent='center'>
                {/* Reserves its space whether or not it is showing, so choosing a theme cannot
                    reflow the row and shift the chip out from under the pointer. */}
                <Check className={cn('check')} sx={{ visibility: isSelected ? 'visible' : 'hidden' }} />
                <Typography variant='body2' color='inherit' lineHeight={1.3} noWrap>{noticeThemeName(theme)}</Typography>
              </HStack>
            </Box>
          );
        })}
      </HStack>
    </Stack>
  );
};
